/**
 * Firebase configuration and initialization
 */
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, enableIndexedDbPersistence } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getPerformance } from 'firebase/performance';
import { createLogger } from '../utils/logger';

const log = createLogger('firebase');

const isLocal = import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS !== 'false';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: isLocal ? 'demo-thrive-companions' : import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app);

// Initialize Performance Monitoring in production only
let perf = null;
if (import.meta.env.PROD) {
  perf = getPerformance(app);
}

// Enable offline persistence in production
// (emulators don't support it well and it causes stale data during dev)
if (!isLocal) {
  enableIndexedDbPersistence(db).catch((err) => {
    log.warn('Firestore persistence unavailable', { code: err.code });
  });
}

// Connect to emulators in development
if (isLocal) {
  log.info('Connecting to Firebase emulators');
  const emulatorHost = location.hostname;
  connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, emulatorHost, 8080);
  connectStorageEmulator(storage, emulatorHost, 9199);
  connectFunctionsEmulator(functions, emulatorHost, 5001);
}

/**
 * Get Firebase Messaging instance (lazy — only when push is needed)
 */
let messagingInstance = null;
const getMessagingInstance = async () => {
  if (messagingInstance) return messagingInstance;
  const { getMessaging } = await import('firebase/messaging');
  messagingInstance = getMessaging(app);
  return messagingInstance;
};

export { app, auth, db, storage, functions, perf, getMessagingInstance };
